/**
 * The browser half of SoundGuys' KEYED owner routes (`pages/api/owner-notes/*` that turn the
 * session's bearer into DealHunt's signed identity): one JSON send and one XHR photo upload, shared
 * by the discussions writer (`lib/community/discussions-api`) and Help & support
 * (`./support-api`).
 *
 * Errors are `OwnerApiError` with DealHunt's reader-safe `{ error, message }`; a signed-out or
 * lapsed session answers 401 `auth_required` / `auth_invalid` → `isSignInError`. Every write
 * forgets the shared `/me` before and after (Directive 0d81c597): a first keyed write mints a
 * claimed profile.
 */
import type { OwnerErrorBody } from 'lib/dealhunt/types'
import { OwnerApiError, bearerAuthHeaders, bearerHeaders, forgetSharedMe, ownerRoute } from './api'
import type { OwnerCredential } from './auth-bridge'

export const KEYED_FALLBACK = 'Something went wrong on our side — try again in a moment.'
export const KEYED_OFFLINE = "We couldn't reach SoundGuys — check your connection and try again."

const SIGN_IN_CODES = new Set(['auth_required', 'auth_invalid'])

/** The write needs a (fresh) sign-in: open the dialog, keep what was typed, try again after. */
export function isSignInError(e: unknown): boolean {
  return e instanceof OwnerApiError && SIGN_IN_CODES.has(e.code)
}

export function parseBody(text: string): unknown {
  if (!text) {
    return null
  }
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

export function apiError(status: number, body: unknown): OwnerApiError {
  const err = (body ?? {}) as Partial<OwnerErrorBody>
  return new OwnerApiError(status, err.error ?? 'error', err.message ?? KEYED_FALLBACK)
}

/** One keyed call (any method); the shared `/me` is forgotten before and after. */
export async function keyedSend<T>(path: string, init: RequestInit): Promise<T> {
  forgetSharedMe()
  try {
    let res: Response
    try {
      res = await fetch(ownerRoute(path), { credentials: 'same-origin', ...init })
    } catch {
      throw new OwnerApiError(0, 'network', KEYED_OFFLINE)
    }
    const body = parseBody(await res.text())
    if (!res.ok) {
      throw apiError(res.status, body)
    }
    return body as T
  } finally {
    forgetSharedMe()
  }
}

export function keyedJson(
  method: string,
  credential: OwnerCredential,
  payload: unknown
): RequestInit {
  return {
    method,
    headers: bearerHeaders(credential),
    body: JSON.stringify(payload),
  }
}

export interface KeyedUpload<T> {
  promise: Promise<T>
  abort: () => void
}

/**
 * One photo (multipart field `photo`) to a keyed media proxy, with upload progress — XHR,
 * because `fetch` reports none. `onProgress` gets 0–1. `accept` reads a 2xx body into the
 * caller's shape, or null when it is not one (→ the fallback error).
 */
export function uploadPhoto<T>(
  path: string,
  credential: OwnerCredential,
  file: File,
  onProgress: (fraction: number) => void,
  accept: (body: unknown) => T | null
): KeyedUpload<T> {
  const xhr = new XMLHttpRequest()
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', ownerRoute(path))
    xhr.withCredentials = true
    // The browser sets the multipart Content-Type with its boundary: auth headers only.
    for (const [name, value] of Object.entries(bearerAuthHeaders(credential))) {
      xhr.setRequestHeader(name, value)
    }
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && e.total > 0) {
        onProgress(Math.min(1, e.loaded / e.total))
      }
    }
    xhr.onerror = () => reject(new OwnerApiError(0, 'network', KEYED_OFFLINE))
    xhr.onabort = () => reject(new OwnerApiError(0, 'aborted', 'Upload cancelled.'))
    xhr.onload = () => {
      forgetSharedMe()
      const body = parseBody(xhr.responseText)
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(apiError(xhr.status, body))
        return
      }
      const accepted = accept(body)
      if (accepted === null) {
        reject(new OwnerApiError(xhr.status, 'error', KEYED_FALLBACK))
        return
      }
      resolve(accepted)
    }
    const form = new FormData()
    form.append('photo', file, file.name)
    forgetSharedMe()
    xhr.send(form)
  })
  return { promise, abort: () => xhr.abort() }
}
